import { PostingArrivalSurface } from '@/features/notifications/components/PostingArrivalSurface';
import { Card, CardContent } from '@/components/ui/card';
import { PostingRecoveryContent } from '@shared/posting/PostingRecoveryContent';
import { cuttingListRecoveryView } from '@shared/posting/postingRecovery';
import type { OutboxPostingStatusDto } from '@shared/posting';

interface CuttingListPostingCardProps {
  // 86d3mnuxe — driven by the OUTBOX status (the posting lifecycle moved off the entity). In-flight /
  // failed only; the posted-success summary lives in the detail panel's own "Submitted" band. null →
  // no active posting job → renders nothing.
  posting: OutboxPostingStatusDto | null;
  /** 86d3mnuxe funnel — NotReached Retry: cancel the failed intent, then re-claim (a genuine
   *  re-decision; the drift dialog may fire). Hidden on a reached failure. */
  onRetry?: () => void;
  /** 86d3mnuxe funnel — Reconcile a REACHED failure: re-arms the same job so the frozen decision
   *  re-executes and the adopt records the Estimate NetSuite already accepted. Never prompts. */
  onReconcile?: () => void;
  /** 86d3mnuxe funnel — close a NotReached failure (Failed → Cancelled) without re-posting; the
   *  draft then accepts a fresh submit. Submitter-level (same permission as the post). Hidden on a
   *  reached failure (the BE refuses it too — abandoning would orphan the Estimate). */
  onCancel?: (() => void) | null;
  isBusy?: boolean;
}

/**
 * N8 (86d3w2kez): CHROME ONLY — the edge-to-edge banding this card needs inside the detail panel, and
 * nothing else. Buttons, reach disclosure and copy come from the shared `PostingRecoveryContent`.
 *
 * ⚠️ The cutting list is the one flow whose local noun and NetSuite noun differ: it posts AS AN
 * ESTIMATE. `cuttingListRecoveryView` carries both, so the failure line says "cutting list" (the thing
 * on screen) while the reach disclosure says "Estimate" (the thing to search for in NetSuite).
 */
export function CuttingListPostingCard({
  posting,
  onRetry,
  onReconcile,
  onCancel,
  isBusy,
}: CuttingListPostingCardProps) {
  if (posting === null) return null;

  const isFailed = posting.status === 'Failed';
  const view = cuttingListRecoveryView(posting);

  return (
    <PostingArrivalSurface
      views={[view]}
      onRetry={onRetry}
      onReconcile={onReconcile}
      onCancel={onCancel ?? undefined}
      isBusy={isBusy}
    >
      <Card
        className={
          isFailed
            ? 'rounded-none border-x-0 border-destructive/30 bg-destructive/10'
            : 'rounded-none border-x-0 border-info/30 bg-info/10'
        }
      >
        <CardContent className="py-4">
          <PostingRecoveryContent
            view={view}
            onRetry={onRetry}
            onReconcile={onReconcile}
            onCancel={onCancel}
            isBusy={isBusy}
          />
        </CardContent>
      </Card>
    </PostingArrivalSurface>
  );
}
